import React, { useContext, useEffect, useMemo, useState } from "react";
import "../css/shop.scss";
import { Link, useSearchParams } from "react-router-dom";
import { FiHeart, FiMinus, FiPlus, FiSearch } from "react-icons/fi";
import productData, { productCategories } from "../Datas/productData";
import { CartContext } from "./CartContext";
import { formatCurrency, siteConfig } from "../config/siteConfig";

const SHUFFLE_STORAGE_KEY = `${siteConfig.storagePrefix}.shop-shuffle`;

const readShuffleSeed = () => {
  try {
    const saved = JSON.parse(window.localStorage.getItem(SHUFFLE_STORAGE_KEY) || "null");
    if (saved?.expires > Date.now() && Number.isFinite(saved.seed)) return saved.seed;
  } catch {
    // A fresh seed works if storage is unavailable.
  }
  return Date.now();
};

function Shop() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { toggleWishlist, isWishlisted, wishlistCount } = useContext(CartContext);
  const [sortMode, setSortMode] = useState("shuffle");
  const [shuffleSeed, setShuffleSeed] = useState(readShuffleSeed);
  const [query, setQuery] = useState(searchParams.get("search") || "");
  const [category, setCategory] = useState(searchParams.get("category") || "All");
  const [maxPrice, setMaxPrice] = useState("all");
  const [filterOpen, setFilterOpen] = useState(
    searchParams.get("view") === "collections" || Boolean(searchParams.get("search"))
  );
  const [wishlistOnly, setWishlistOnly] = useState(searchParams.get("wishlist") === "1");
  const [loadedImages, setLoadedImages] = useState([]);
  const [failedImages, setFailedImages] = useState([]);

  useEffect(() => {
    setQuery(searchParams.get("search") || "");
    setWishlistOnly(searchParams.get("wishlist") === "1");
    if (searchParams.get("view") === "collections") setFilterOpen(true);
  }, [searchParams]);

  useEffect(() => {
    try {
      window.localStorage.setItem(
        SHUFFLE_STORAGE_KEY,
        JSON.stringify({ seed: shuffleSeed, expires: Date.now() + 86_400_000 })
      );
    } catch {
      // Sorting remains available without persistence.
    }
  }, [shuffleSeed]);

  const visibleProducts = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();
    const filtered = productData.filter((product) => {
      const searchable = [
        product.name,
        product.maker,
        product.category,
        ...product.tags,
      ]
        .join(" ")
        .toLowerCase();
      const matchesQuery = !normalizedQuery || searchable.includes(normalizedQuery);
      const matchesCategory = category === "All" || product.category === category;
      const matchesPrice = maxPrice === "all" || product.price <= Number(maxPrice);
      const matchesWishlist = !wishlistOnly || isWishlisted(product.id);
      return matchesQuery && matchesCategory && matchesPrice && matchesWishlist;
    });

    return [...filtered].sort((first, second) => {
      if (sortMode === "lowest") return first.price - second.price;
      if (sortMode === "highest") return second.price - first.price;
      const firstScore = Math.sin(first.id * 9301 + shuffleSeed) * 10000;
      const secondScore = Math.sin(second.id * 9301 + shuffleSeed) * 10000;
      return firstScore - secondScore;
    });
  }, [category, isWishlisted, maxPrice, query, shuffleSeed, sortMode, wishlistOnly]);

  const clearFilters = () => {
    setQuery("");
    setCategory("All");
    setMaxPrice("all");
    setWishlistOnly(false);
    setSearchParams({});
  };

  const selectSort = (mode) => {
    setSortMode(mode);
    if (mode === "shuffle") setShuffleSeed(Date.now() + Math.random());
  };

  return (
    <main id="main-content" className="shop-page">
      <h1 className="sr-only">Shop all home and lifestyle products</h1>

      <div className="shop-toolbar" aria-label="Shop controls">
        <div className="shop-toolbar__sort" role="group" aria-label="Sort products">
          {[
            ["shuffle", "Shuffle"],
            ["lowest", "Lowest"],
            ["highest", "Highest"],
          ].map(([value, label]) => (
            <button
              type="button"
              key={value}
              className={sortMode === value ? "is-active" : ""}
              aria-pressed={sortMode === value}
              onClick={() => selectSort(value)}
            >
              {label}
            </button>
          ))}
        </div>

        <div className="shop-toolbar__right">
          <span aria-live="polite">{visibleProducts.length} objects</span>
          <button
            type="button"
            aria-expanded={filterOpen}
            aria-controls="shop-filters"
            onClick={() => setFilterOpen((open) => !open)}
          >
            {filterOpen ? <FiMinus aria-hidden="true" /> : <FiPlus aria-hidden="true" />}
            Filters
          </button>
        </div>
      </div>

      <section
        id="shop-filters"
        className={`shop-filters${filterOpen ? " is-open" : ""}`}
        aria-hidden={!filterOpen}
      >
        <div className="shop-filters__search">
          <label htmlFor="collection-search">Search</label>
          <div>
            <FiSearch aria-hidden="true" />
            <input
              id="collection-search"
              type="search"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Object, material or maker"
              tabIndex={filterOpen ? 0 : -1}
            />
          </div>
        </div>

        <fieldset>
          <legend>Collection</legend>
          <div className="shop-filters__options">
            {productCategories.map((item) => (
              <button
                type="button"
                key={item}
                className={category === item ? "is-active" : ""}
                aria-pressed={category === item}
                onClick={() => setCategory(item)}
                tabIndex={filterOpen ? 0 : -1}
              >
                {item}
              </button>
            ))}
          </div>
        </fieldset>

        <fieldset>
          <legend>Price</legend>
          <div className="shop-filters__options">
            {[
              ["all", "All prices"],
              ["75", "Under $75"],
              ["125", "Under $125"],
              ["180", "Up to $180"],
            ].map(([value, label]) => (
              <button
                type="button"
                key={value}
                className={maxPrice === value ? "is-active" : ""}
                aria-pressed={maxPrice === value}
                onClick={() => setMaxPrice(value)}
                tabIndex={filterOpen ? 0 : -1}
              >
                {label}
              </button>
            ))}
          </div>
        </fieldset>

        <fieldset>
          <legend>Saved</legend>
          <button
            type="button"
            className={`shop-filters__wishlist${wishlistOnly ? " is-active" : ""}`}
            aria-pressed={wishlistOnly}
            onClick={() => setWishlistOnly((value) => !value)}
            tabIndex={filterOpen ? 0 : -1}
          >
            Wishlist ({wishlistCount})
          </button>
        </fieldset>

        <button
          className="shop-filters__clear"
          type="button"
          onClick={clearFilters}
          tabIndex={filterOpen ? 0 : -1}
        >
          Clear all
        </button>
      </section>

      {visibleProducts.length > 0 ? (
        <section className="product-grid" aria-label="Products">
          {visibleProducts.map((product) => {
            const imageLoaded = loadedImages.includes(product.id);
            const imageFailed = failedImages.includes(product.id);

            return (
              <article className="product-card" key={product.id}>
                <div className="product-card__visual">
                  <Link
                    className={`product-card__image${imageLoaded ? " is-loaded" : ""}${
                      imageFailed ? " image-fallback" : ""
                    }`}
                    to={`/shopDetails/${product.id}`}
                    aria-label={`View ${product.name}`}
                  >
                    {!imageFailed && (
                      <img
                        src={product.image}
                        alt={`${product.name} by ${product.maker}`}
                        width="1120"
                        height="1400"
                        decoding="async"
                        loading="lazy"
                        onLoad={() =>
                          setLoadedImages((items) =>
                            items.includes(product.id) ? items : [...items, product.id]
                          )
                        }
                        onError={() =>
                          setFailedImages((items) =>
                            items.includes(product.id) ? items : [...items, product.id]
                          )
                        }
                      />
                    )}
                  </Link>
                  <button
                    className={`product-card__wishlist${
                      isWishlisted(product.id) ? " is-active" : ""
                    }`}
                    type="button"
                    onClick={() => toggleWishlist(product.id)}
                    aria-label={`${
                      isWishlisted(product.id) ? "Remove" : "Add"
                    } ${product.name} ${isWishlisted(product.id) ? "from" : "to"} wishlist`}
                  >
                    <FiHeart aria-hidden="true" />
                  </button>
                  {product.stock <= 2 && <span className="product-card__edition">Small edition</span>}
                </div>

                <Link className="product-card__info" to={`/shopDetails/${product.id}`}>
                  <div>
                    <h2>{product.name}</h2>
                    <span>{formatCurrency(product.price)}</span>
                  </div>
                  <p>{product.maker}</p>
                </Link>
              </article>
            );
          })}
        </section>
      ) : (
        <section className="shop-empty" aria-live="polite">
          <p className="eyebrow">Nothing found</p>
          <h2>No objects match those filters.</h2>
          <button type="button" className="button-outline" onClick={clearFilters}>
            Reset the collection
          </button>
        </section>
      )}
    </main>
  );
}

export default Shop;
